import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { LocaleDebug } from './components'
import { locale as liblocale } from './index'
import { supportedLanguagesByLocaleCode } from './locales'
import { LiveLanguageCodes, Locale, ResolvedLocale, Source } from './types'
import { shouldShowOverrideDialogue } from './utils'

// React Context Types
type LocaleContextValue = {
  /** Current resolved locale with source information */
  resolvedLocale: ResolvedLocale
  /** Full locale object with metadata (flag, name, country, etc.) */
  fullLocale: Locale
  /** Update the locale by passing in your chosen value */
  updateLocale: (locale: LiveLanguageCodes) => void
}

type LocaleProviderProps = {
  children: React.ReactNode
  /** Server-resolved locale passed from _app.tsx#getInitialProps() */
  initialLocale: ResolvedLocale | null
  /**
   * Automatically update document.documentElement.lang when locale changes.
   * Ensures proper accessibility for screen readers and search engines.
   * @default false
   */
  updateHtmlLang?: boolean
}

// Fallback locale - same as in index.ts
const FALLBACK_LOCALE: ResolvedLocale = {
  localeCode: supportedLanguagesByLocaleCode['en-GB'].localeCode,
  source: Source.FALLBACK,
  fromCookie: false,
}

// Create the context
const LocaleContext = createContext<LocaleContextValue | null>(null)

/**
 * Unified LocaleProvider that coordinates server and client locale resolution
 *
 * Features:
 * - Accepts server-resolved locale to prevent hydration mismatches
 * - Provides rich locale information beyond just localeCode
 * - Optimizes API calls by only refreshing when needed
 * - Tracks resolution source for debugging and analytics
 */
export const LocaleProvider = ({
  children,
  initialLocale,
  updateHtmlLang = false,
}: LocaleProviderProps) => {
  const [updatedLocale, setUpdatedLocale] = useState<ResolvedLocale | null>(
    null,
  )

  const browserLocale =
    typeof window !== 'undefined' ? liblocale.fromBrowser() : null
  const resolvedLocale =
    updatedLocale ?? initialLocale ?? browserLocale ?? FALLBACK_LOCALE

  // Get full locale object with rich metadata
  const fullLocale = useMemo(
    () => liblocale.getLocale(resolvedLocale.localeCode),
    [resolvedLocale.localeCode],
  )

  // Refresh locale when user context changes (e.g., business context switch)
  const updateLocale = useCallback(
    async (locale: LiveLanguageCodes) => {
      // Only update if the locale actually changed
      if (locale !== resolvedLocale.localeCode) {
        console.info(
          `[lib.locale] Locale changed from ${resolvedLocale.localeCode} to ${locale}`,
        )

        // Set the locale value to the latest one passed in by the app
        setUpdatedLocale({ localeCode: locale, source: Source.APP_OVERRIDE })
      } else {
        console.info(
          `[lib.locale] Locale unchanged: ${resolvedLocale.localeCode}`,
        )
      }
    },
    [resolvedLocale.localeCode],
  )

  // Automatically update HTML lang attribute if enabled
  useEffect(() => {
    if (updateHtmlLang && typeof document !== 'undefined') {
      const newLang = resolvedLocale.localeCode
      if (document.documentElement.lang !== newLang) {
        document.documentElement.lang = newLang
        console.info('[lib.locale] Updated HTML lang to:', newLang)
      }
    }
  }, [updateHtmlLang, resolvedLocale.localeCode])

  const contextValue: LocaleContextValue = {
    resolvedLocale,
    fullLocale,
    updateLocale,
  }

  const showOverrideDialogue = shouldShowOverrideDialogue()

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
      {showOverrideDialogue && <LocaleDebug />}
    </LocaleContext.Provider>
  )
}

/**
 * Hook to access the unified locale context
 *
 * Provides:
 * - resolvedLocale: Current locale with source information
 * - fullLocale: Rich locale object with flag, name, country metadata
 * - isUpdating: Whether locale is currently being refreshed
 * - updateLocale: Function to update the locale to a passed in value
 * - resolutionSource: Debug info about how locale was resolved
 */
export const useLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext)

  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }

  return context
}

/**
 * Helper hook that just returns the locale code string
 * For components that only need the basic locale code
 */
export const useLocaleCode = () => {
  const { resolvedLocale } = useLocale()
  return resolvedLocale.localeCode
}

/**
 * Constants that define the header that is expected by the backend
 * when the web app's choice of locale should drive how the backend localises the response.
 * The backend expects the associated value to be a BCP 47 language tag (e.g. "en-GB").
 * This is typically used when the user is unauthenticated or has just begun a signup flow.
 * These are the same value currently, but are defined separately
 * to allow for greater semantic meaning & for divergence in the future if needed.
 */
export const preSignupLocaleHeader = 'Monzo-Pre-Signup-Current-Language'
export const anonymousLocaleHeader = 'Monzo-Pre-Signup-Current-Language'
